/**
 * PlatenVariantsSection (Phase 86 Round 4): what Large and Small are on each
 * platen, per org. Settings, Production, Printing & Files, card id
 * `platen-variants`, between Platen size and Print size rules.
 *
 * Copy and layout are the approved Gate U mockup (86-31-UI-REVIEW.md):
 *   - one stacked card per platen ("14x16 platen"), badges Default / Changed;
 *   - row "Large": Width x Height px, the inches at 300 DPI and the aspect;
 *   - row "Default for Small": "Uploaded by hand" | "Made from Large"; Made
 *     from Large has ONE percent input linked both ways to the Small art px
 *     (no slider), EMPTY when first chosen (R4-11);
 *   - a preview drawn with `smallPlacement` (aria-hidden; the lines say it);
 *   - one Save for all three platens (CAS on `updatedAt`), Discard, the
 *     blockers list, the conflict Alert with Reload, and a read-only view.
 *
 * Every number on screen comes from the shared platen-pixels module
 * (`smallArtPixels`, `formatScalePercent`, `smallPlacement`), so what this
 * card shows is what product sync makes. The server is authoritative: strict
 * zod, `assertOwnerOrSelf` on every target org, admins only for the write.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, AlertTriangle, Info, Loader2, RefreshCw, Save } from 'lucide-react';
import {
  DTG_PLATEN_CODES,
  PLATEN_PIXEL_BOUNDS,
  defaultLargePixels,
  formatScalePercent,
  isLargeAboveNominal,
  isValidLargePx,
  isValidScaleBp,
  platenNominalInches,
  scaleBpFromPercentText,
  scaleBpFromSmallPx,
  smallArtPixels,
  smallPlacement,
  type DtgPlatenCode,
  type EffectivePlatenPixels,
  type PlatenPixelSize,
} from '@threadheads/shared';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { useContainerNarrowerThan } from '@/hooks/useContainerWidth';
import { normalizeNumericText } from '@/lib/parse-kg';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { Skeleton } from '@/components/ui/skeleton';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useOwnerClientTarget } from '../hooks/useOwnerClientTarget';
import { LinkedLine } from './console/SectionLink';

const SAVE_ERROR_TOAST_ID = 'platen-variants-save-error';
const PX_PER_INCH = 300;
const WHOLE_RE = /^\d+$/;

// ---------------------------------------------------------------------------
// The edit buffer: one draft per platen, all text, parsed on read.
// ---------------------------------------------------------------------------

type SmallMode = 'hand' | 'made';

interface PlatenDraft {
  width: string;
  height: string;
  mode: SmallMode;
  pct: string;
  artWidth: string;
  artHeight: string;
  /** The Small art side typed out of range (blocks Save until fixed). */
  artError: 'width' | 'height' | null;
}

type DraftMap = Record<DtgPlatenCode, PlatenDraft>;

const parseWhole = (text: string): number | null => {
  const s = normalizeNumericText(text);
  return WHOLE_RE.test(s) ? Number(s) : null;
};

/** Large px, or null when it is not a whole number in bounds. */
const parseLargeSide = (text: string): number | null => {
  const n = parseWhole(text);
  return isValidLargePx(n) ? n : null;
};

/** The percent as basis points: 'empty', 'invalid' (also out of range) or bp. */
const parsePercent = (text: string): number | 'empty' | 'invalid' => {
  const bp = scaleBpFromPercentText(normalizeNumericText(text));
  if (bp === null) return 'empty';
  if (bp === 'invalid' || !isValidScaleBp(bp)) return 'invalid';
  return bp;
};

const largeOf = (draft: PlatenDraft): PlatenPixelSize | null => {
  const width = parseLargeSide(draft.width);
  const height = parseLargeSide(draft.height);
  return width !== null && height !== null ? { width, height } : null;
};

/** Small art bounds per side for a Large (10% to 100%). */
const artBounds = (large: PlatenPixelSize, side: 'width' | 'height'): { min: number; max: number } => ({
  min: smallArtPixels(large, PLATEN_PIXEL_BOUNDS.minScaleBp)[side],
  max: large[side],
});

function draftFrom(large: PlatenPixelSize, smallScaleBp: number | null): PlatenDraft {
  const art = smallScaleBp !== null ? smallArtPixels(large, smallScaleBp) : null;
  return {
    width: String(large.width),
    height: String(large.height),
    mode: smallScaleBp !== null ? 'made' : 'hand',
    pct: smallScaleBp !== null ? formatScalePercent(smallScaleBp) : '',
    artWidth: art ? String(art.width) : '',
    artHeight: art ? String(art.height) : '',
    artError: null,
  };
}

function toDrafts(pixels: EffectivePlatenPixels): DraftMap {
  const out = {} as DraftMap;
  for (const platen of DTG_PLATEN_CODES) {
    out[platen] = draftFrom(pixels[platen].large, pixels[platen].smallScaleBp);
  }
  return out;
}

/** Re-render the Small art px from the percent (or clear them). */
function withArtFromPercent(draft: PlatenDraft): PlatenDraft {
  const large = largeOf(draft);
  const bp = parsePercent(draft.pct);
  if (!large || typeof bp !== 'number') return { ...draft, artWidth: '', artHeight: '', artError: null };
  const art = smallArtPixels(large, bp);
  return { ...draft, artWidth: String(art.width), artHeight: String(art.height), artError: null };
}

interface DraftStatus {
  large: PlatenPixelSize | null;
  widthBad: boolean;
  heightBad: boolean;
  bp: number | 'empty' | 'invalid' | null;
  /** The value Save sends, or null while something blocks. */
  value: { large: PlatenPixelSize; smallScaleBp: number | null } | null;
}

function statusOf(draft: PlatenDraft): DraftStatus {
  const large = largeOf(draft);
  const widthBad = parseLargeSide(draft.width) === null;
  const heightBad = parseLargeSide(draft.height) === null;
  const bp = draft.mode === 'made' ? parsePercent(draft.pct) : null;
  let value: DraftStatus['value'] = null;
  if (large) {
    if (draft.mode === 'hand') value = { large, smallScaleBp: null };
    else if (typeof bp === 'number' && draft.artError === null) value = { large, smallScaleBp: bp };
  }
  return { large, widthBad, heightBad, bp, value };
}

// ---------------------------------------------------------------------------
// Section.
// ---------------------------------------------------------------------------

export function PlatenVariantsSection(): JSX.Element {
  const { t } = useTranslation('settings');
  const { isAdmin } = useRole();
  const admin = isAdmin();
  const { ownOrgId, clientOptions, selectedOrgId, setSelectedOrgId, targetOrgInput } = useOwnerClientTarget();

  const pixelsQuery = trpc.platenSizeRules.getPixels.useQuery(
    { organizationId: targetOrgInput },
    { enabled: !!selectedOrgId },
  );
  const utils = trpc.useUtils();

  const [baseline, setBaseline] = useState<EffectivePlatenPixels | null>(null);
  const [drafts, setDrafts] = useState<DraftMap | null>(null);
  const [baseUpdatedAt, setBaseUpdatedAt] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  // Unsaved edits, read synchronously by the hydrate effect.
  const dirtyRef = useRef(false);
  // The cards wrapper: focus lands here after Save or Discard (the buttons
  // that had it are gone with the save row).
  const cardsRef = useRef<HTMLDivElement>(null);
  const focusCards = (): void => cardsRef.current?.focus({ preventScroll: true });

  // A client switch drops the buffer at once, before the new org answers, so
  // a Save in that gap can never write one org's numbers to another.
  useEffect(() => {
    setBaseline(null);
    setDrafts(null);
    setBaseUpdatedAt(null);
    setConflict(false);
    dirtyRef.current = false;
  }, [selectedOrgId]);

  const hydrate = useCallback((data: { pixels: EffectivePlatenPixels; updatedAt: string | null }) => {
    setBaseline(data.pixels);
    setDrafts(toDrafts(data.pixels));
    setBaseUpdatedAt(data.updatedAt);
    dirtyRef.current = false;
  }, []);

  // Hydrate from the server, but never over unsaved edits (a background
  // refetch must not wipe what the operator is typing; a stale Save is then
  // refused by the server instead).
  useEffect(() => {
    if (!pixelsQuery.data || dirtyRef.current) return;
    hydrate(pixelsQuery.data);
  }, [pixelsQuery.data, hydrate]);

  const statuses = useMemo(() => {
    if (!drafts) return null;
    const out = {} as Record<DtgPlatenCode, DraftStatus>;
    for (const platen of DTG_PLATEN_CODES) out[platen] = statusOf(drafts[platen]);
    return out;
  }, [drafts]);

  const changedOf = useCallback(
    (platen: DtgPlatenCode): boolean => {
      if (!baseline || !statuses) return false;
      const value = statuses[platen].value;
      const base = baseline[platen];
      if (!value) return true;
      return (
        value.large.width !== base.large.width ||
        value.large.height !== base.large.height ||
        value.smallScaleBp !== base.smallScaleBp
      );
    },
    [baseline, statuses],
  );

  const hasChanges = DTG_PLATEN_CODES.some((platen) => changedOf(platen));
  dirtyRef.current = hasChanges;

  const blockers = useMemo(() => {
    if (!drafts || !statuses) return [];
    const list: string[] = [];
    for (const platen of DTG_PLATEN_CODES) {
      const s = statuses[platen];
      if (s.widthBad) list.push(t('platenVariants.blkLw', { platen }));
      if (s.heightBad) list.push(t('platenVariants.blkLh', { platen }));
      if (drafts[platen].mode === 'made') {
        if (typeof s.bp !== 'number') list.push(t('platenVariants.blkPct', { platen }));
        else if (drafts[platen].artError) list.push(t('platenVariants.blkArt', { platen }));
      }
    }
    return list;
  }, [drafts, statuses, t]);

  // Leaving the page with unsaved edits asks first.
  useEffect(() => {
    if (!admin || !hasChanges) return undefined;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [admin, hasChanges]);

  const updateMutation = trpc.platenSizeRules.updatePixels.useMutation({
    onSuccess: (result, variables) => {
      toast.dismiss(SAVE_ERROR_TOAST_ID);
      toast.success(t('platenVariants.saved'));
      setConflict(false);
      const saved = { pixels: result.pixels, updatedAt: result.updatedAt };
      hydrate(saved);
      // The save answers the new doc and version: seed the read with it
      // instead of a second round trip.
      utils.platenSizeRules.getPixels.setData({ organizationId: variables.organizationId }, saved);
      focusCards();
    },
    // The buffer is kept: nothing is reset on failure.
    onError: (e) => {
      if ((e as { data?: { code?: string } | null }).data?.code === 'CONFLICT') {
        toast.dismiss(SAVE_ERROR_TOAST_ID);
        setConflict(true);
        return;
      }
      toast.error(t('platenVariants.saveError'), {
        id: SAVE_ERROR_TOAST_ID,
        duration: Infinity,
      });
    },
  });

  const editDraft = (platen: DtgPlatenCode, next: (draft: PlatenDraft) => PlatenDraft): void => {
    setDrafts((prev) => (prev ? { ...prev, [platen]: next(prev[platen]) } : prev));
  };

  const handleSave = (): void => {
    if (!statuses || !selectedOrgId || blockers.length > 0) return;
    const pixels = {} as Record<DtgPlatenCode, { large: PlatenPixelSize; smallScaleBp: number | null }>;
    for (const platen of DTG_PLATEN_CODES) {
      const value = statuses[platen].value;
      if (!value) return;
      pixels[platen] = value;
    }
    updateMutation.mutate({ organizationId: targetOrgInput, pixels, expectedUpdatedAt: baseUpdatedAt });
  };

  const handleDiscard = (): void => {
    if (!baseline) return;
    setDrafts(toDrafts(baseline));
    focusCards();
  };

  // A failed Reload keeps the edits and the conflict (refetch keeps the old
  // data on error, so only a successful answer may replace the buffer).
  const handleReload = async (): Promise<void> => {
    const result = await pixelsQuery.refetch();
    if (result.isSuccess && result.data) {
      hydrate(result.data);
      setConflict(false);
    }
  };

  // Nothing loaded yet and the read failed: the whole-section error. A failed
  // refetch over loaded cards is the inline error above them instead, so the
  // edits stay on screen.
  const loadFailed = pixelsQuery.isError && !drafts;
  const pending = !selectedOrgId || pixelsQuery.isPending || !drafts || !baseline;
  const saving = updateMutation.isPending;
  const blockedText = t('platenVariants.saveBlocked', { list: blockers.join(t('platenVariants.listSep')) });

  return (
    <div className="space-y-4">
      <p className="flex items-start gap-2 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="max-w-prose leading-relaxed">
          <LinkedLine
            text={(link) => t('platenVariants.framing', { link })}
            linkText={t('platenVariants.framingLink')}
            sectionId="platen-size"
          />
        </span>
      </p>

      {/* Applies to: a 3PL owner only (it has clients to set this for). */}
      {clientOptions.length > 0 && (
        <div className="grid max-w-xs gap-1.5">
          <label htmlFor="platen-variants-client" className="text-sm font-medium">
            {t('platenVariants.appliesTo')}
          </label>
          <Select value={selectedOrgId ?? ownOrgId} onValueChange={setSelectedOrgId} disabled={!ownOrgId || saving}>
            <SelectTrigger id="platen-variants-client" className="h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ownOrgId && <SelectItem value={ownOrgId}>{t('platenVariants.thisOrganization')}</SelectItem>}
              {clientOptions.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {loadFailed ? (
        <div className="space-y-3">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{t('platenVariants.loadError')}</AlertDescription>
          </Alert>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void pixelsQuery.refetch()}
            disabled={pixelsQuery.isFetching}
            className="gap-1.5"
          >
            {pixelsQuery.isFetching && (
              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            )}
            {t('platenVariants.retry')}
          </Button>
        </div>
      ) : pending ? (
        <div className="max-w-3xl space-y-3" aria-busy="true">
          <Skeleton className="h-[196px] w-full rounded-lg" />
          <Skeleton className="h-[196px] w-full rounded-lg" />
          <Skeleton className="h-[196px] w-full rounded-lg" />
        </div>
      ) : (
        <div className="max-w-3xl space-y-4">
          {pixelsQuery.isError && (
            <SectionError
              variant="inline"
              title={t('platenVariants.loadError')}
              onRetry={() => void pixelsQuery.refetch()}
              retryLabel={t('platenVariants.retry')}
              retrying={pixelsQuery.isFetching}
              data-testid="platen-variants-refetch-error"
            />
          )}

          <div ref={cardsRef} tabIndex={-1} className="space-y-3 outline-none" data-testid="platen-variants-cards">
            {DTG_PLATEN_CODES.map((platen) => (
              <PlatenCard
                key={platen}
                platen={platen}
                draft={drafts[platen]}
                status={statuses![platen]}
                saved={baseline[platen]}
                changed={changedOf(platen)}
                admin={admin}
                disabled={saving}
                onEdit={(next) => editDraft(platen, next)}
              />
            ))}
          </div>

          {admin && conflict && (
            <Alert className="border-amber-300 bg-amber-50 text-amber-900" data-testid="platen-variants-conflict">
              <AlertTriangle className="h-4 w-4 text-amber-700" aria-hidden="true" />
              <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
                <span className="space-y-0.5">
                  <span className="block font-medium">{t('platenVariants.conflict')}</span>
                  <span className="block text-xs">{t('platenVariants.conflictKeep')}</span>
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleReload()}
                  disabled={pixelsQuery.isFetching}
                  className="gap-1.5 border-amber-300 bg-background motion-reduce:transition-none"
                >
                  {pixelsQuery.isFetching ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {t('platenVariants.reload')}
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {admin && hasChanges && (
            <div className="space-y-2" data-testid="platen-variants-save-row">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <p className="text-xs text-muted-foreground sm:flex-1">{t('platenVariants.saveNote')}</p>
                {/* Phone: stacked, full width, Save on top. */}
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleDiscard}
                    disabled={saving}
                    className="w-full motion-reduce:transition-none sm:w-auto"
                  >
                    {t('platenVariants.discard')}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSave}
                    disabled={saving || blockers.length > 0 || conflict || pixelsQuery.isFetching}
                    aria-describedby="platen-variants-save-blockers"
                    className="w-full gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90 motion-reduce:transition-none sm:w-auto"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    ) : (
                      <Save className="h-4 w-4" aria-hidden="true" />
                    )}
                    {saving ? t('platenVariants.saving') : t('platenVariants.save')}
                  </Button>
                </div>
              </div>
              {/* Always mounted (a live region that comes and goes is not
                  announced); hidden while nothing blocks. */}
              <p
                id="platen-variants-save-blockers"
                role="status"
                className={cn('text-xs font-medium text-amber-800 sm:text-right', blockers.length === 0 && 'sr-only')}
              >
                {blockers.length > 0 ? blockedText : ''}
              </p>
            </div>
          )}
        </div>
      )}

      {!admin && !pending && !loadFailed && (
        <p className="text-sm text-muted-foreground">{t('platenVariants.nonAdmin')}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One platen.
// ---------------------------------------------------------------------------

/** The field's state, loudest first: it owns the border, and the border holds on focus. */
type FieldState = 'bad' | 'warn' | 'changed' | null;

const numberInputClass =
  'h-10 w-24 tabular-nums focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const borderFor = (state: FieldState): string | undefined => {
  switch (state) {
    case 'bad':
      return 'border-destructive focus-visible:border-destructive';
    case 'warn':
      return 'border-amber-300 focus-visible:border-amber-300';
    case 'changed':
      return 'border-threadheads-purple focus-visible:border-threadheads-purple';
    default:
      return undefined;
  }
};

const fieldState = (bad: boolean, warn: boolean, changed: boolean): FieldState =>
  bad ? 'bad' : warn ? 'warn' : changed ? 'changed' : null;

/** Space-joined ids for `aria-describedby`, or undefined when none apply. */
const describedBy = (...ids: Array<string | false | null | undefined>): string | undefined => {
  const list = ids.filter((id): id is string => typeof id === 'string' && id.length > 0);
  return list.length > 0 ? list.join(' ') : undefined;
};

function PxField({
  value,
  ariaLabel,
  state,
  describedById,
  disabled,
  onChange,
  onBlur,
  suffix,
}: {
  value: string;
  ariaLabel: string;
  state: FieldState;
  describedById?: string;
  disabled?: boolean;
  onChange: (text: string) => void;
  onBlur?: () => void;
  suffix: string;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={value}
        aria-label={ariaLabel}
        aria-invalid={state === 'bad' || undefined}
        aria-describedby={describedById}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={cn(numberInputClass, borderFor(state))}
      />
      <span className="text-sm text-muted-foreground">{suffix}</span>
    </span>
  );
}

/** The Large meta line in parts (split on " · "), each kept on one line. */
function largeMetaParts(
  t: (key: string, options?: Record<string, unknown>) => string,
  platen: DtgPlatenCode,
  large: PlatenPixelSize,
): string[] {
  let meta = t('platenVariants.largeMeta', {
    wIn: (large.width / PX_PER_INCH).toFixed(1),
    hIn: (large.height / PX_PER_INCH).toFixed(1),
    aspect: (large.width / large.height).toFixed(3),
  });
  const d = defaultLargePixels(platen);
  if (d.width !== large.width || d.height !== large.height) {
    meta += t('platenVariants.largeMetaDefault', { w: d.width, h: d.height });
  }
  return meta
    .split(' · ')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function MetaLine({ id, parts }: { id?: string; parts: string[] }): JSX.Element {
  return (
    <p id={id} className="text-xs tabular-nums text-muted-foreground">
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 && ' · '}
          <span className="whitespace-nowrap">{part}</span>
        </span>
      ))}
    </p>
  );
}

/**
 * One labelled row: the label column (5.5rem) beside the fields, stacked when
 * narrow. `labelClassName` nudges the label onto the first control's line.
 */
function CardRow({
  label,
  narrow,
  labelClassName,
  children,
}: {
  label: string;
  narrow: boolean;
  labelClassName?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={narrow ? 'space-y-2' : 'grid grid-cols-[5.5rem_minmax(0,1fr)] items-start gap-x-3'}>
      <p className={cn('text-sm font-semibold text-foreground', !narrow && labelClassName)}>{label}</p>
      <div className="min-w-0 space-y-2">{children}</div>
    </div>
  );
}

function FieldLabel({ children }: { children: ReactNode }): JSX.Element {
  return <span className="text-xs text-foreground">{children}</span>;
}

function PlatenCard({
  platen,
  draft,
  status,
  saved,
  changed,
  admin,
  disabled,
  onEdit,
}: {
  platen: DtgPlatenCode;
  draft: PlatenDraft;
  status: DraftStatus;
  saved: EffectivePlatenPixels[DtgPlatenCode];
  changed: boolean;
  admin: boolean;
  disabled: boolean;
  onEdit: (next: (draft: PlatenDraft) => PlatenDraft) => void;
}): JSX.Element {
  const { t } = useTranslation('settings');
  const [articleRef, narrowOrNull] = useContainerNarrowerThan<HTMLElement>(480);
  const narrow = narrowOrNull === true;
  const pctInputRef = useRef<HTMLInputElement>(null);
  const [focusPct, setFocusPct] = useState(false);
  useEffect(() => {
    if (!focusPct) return;
    pctInputRef.current?.focus();
    setFocusPct(false);
  }, [focusPct]);

  const { large, bp } = status;
  const d = defaultLargePixels(platen);
  const isDefault =
    status.value !== null &&
    status.value.large.width === d.width &&
    status.value.large.height === d.height &&
    status.value.smallScaleBp === null;
  const nominal = platenNominalInches(platen);
  const big = large !== null && isLargeAboveNominal(platen, large);
  const widthBig = large !== null && large.width > nominal.width * PX_PER_INCH;
  const heightBig = large !== null && large.height > nominal.height * PX_PER_INCH;

  const widthChanged = parseLargeSide(draft.width) !== saved.large.width;
  const heightChanged = parseLargeSide(draft.height) !== saved.large.height;
  const modeChanged = (draft.mode === 'made') !== (saved.smallScaleBp !== null);
  const pctChanged = draft.mode === 'made' && bp !== saved.smallScaleBp;

  const id = (part: string): string => `platen-variants-${platen}-${part}`;
  const largeBad = status.widthBad || status.heightBad;
  const largeDescribedBy = describedBy(largeBad && id('large-error'), big && id('large-warn'), large && id('large-meta'));
  const smallMetaId = large ? id('small-meta') : null;

  const setLarge = (side: 'width' | 'height', text: string): void =>
    onEdit((prev) => {
      const next = { ...prev, [side]: text };
      return next.mode === 'made' ? withArtFromPercent(next) : next;
    });

  const normalizeLarge = (side: 'width' | 'height'): void =>
    onEdit((prev) => {
      const n = parseLargeSide(prev[side]);
      return n === null ? prev : { ...prev, [side]: String(n) };
    });

  const setMode = (mode: SmallMode): void => {
    if (mode === draft.mode) return;
    // A percent typed before switching away comes back; otherwise the saved
    // percent; otherwise EMPTY (R4-11), and the cursor goes to it.
    const pct =
      mode === 'made'
        ? draft.pct.trim() !== ''
          ? draft.pct
          : saved.smallScaleBp !== null
            ? formatScalePercent(saved.smallScaleBp)
            : ''
        : draft.pct;
    onEdit((prev) => {
      if (mode === prev.mode) return prev;
      if (mode === 'hand') return { ...prev, mode, artError: null };
      return withArtFromPercent({ ...prev, mode, pct });
    });
    if (mode === 'made' && pct === '') setFocusPct(true);
  };

  const setPct = (text: string): void => onEdit((prev) => withArtFromPercent({ ...prev, pct: text }));

  const normalizePct = (): void =>
    onEdit((prev) => {
      const p = parsePercent(prev.pct);
      return typeof p === 'number' ? { ...prev, pct: formatScalePercent(p) } : prev;
    });

  const setArt = (side: 'width' | 'height', text: string): void =>
    onEdit((prev) => {
      const key = side === 'width' ? 'artWidth' : 'artHeight';
      const next: PlatenDraft = { ...prev, [key]: text };
      // Clearing the Small art is the same as clearing the percent: the amber
      // "Enter a percent" line, not a red range error.
      if (normalizeNumericText(text).trim() === '') {
        return { ...next, pct: '', artWidth: '', artHeight: '', artError: null };
      }
      const l = largeOf(prev);
      if (!l) return next;
      const px = parseWhole(text);
      const bounds = artBounds(l, side);
      if (px === null || px < bounds.min || px > bounds.max) return { ...next, artError: side };
      const scale = scaleBpFromSmallPx(px, l[side]);
      const art = smallArtPixels(l, scale);
      const other = side === 'width' ? 'artHeight' : 'artWidth';
      return { ...next, pct: formatScalePercent(scale), [other]: String(art[side === 'width' ? 'height' : 'width']), artError: null };
    });

  const normalizeArt = (): void =>
    onEdit((prev) => (prev.artError === null ? withArtFromPercent(prev) : prev));

  const art = large && typeof bp === 'number' ? smallArtPixels(large, bp) : null;
  const pctEmpty = bp === 'empty';
  const pctBad = bp === 'invalid';
  const artBad = large !== null && draft.artError !== null;
  const pctDescribedBy = describedBy(pctEmpty && id('pct-needs'), pctBad && id('pct-error'), smallMetaId);
  const artDescribedBy = describedBy(artBad && id('art-error'), pctEmpty && id('pct-needs'), smallMetaId);

  return (
    <article
      ref={articleRef}
      className="rounded-lg border bg-card p-4"
      aria-labelledby={id('title')}
      data-testid={`platen-variants-card-${platen}`}
    >
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <h4 id={id('title')} className="text-sm font-semibold">
          <span className="font-mono">{platen}</span> {t('platenVariants.platen')}
        </h4>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {isDefault && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
              {t('platenVariants.badgeDefault')}
            </span>
          )}
          {admin && changed && (
            <span className="rounded border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary-strong">
              {t('platenVariants.badgeChanged')}
            </span>
          )}
        </span>
      </header>

      <div className={cn('flex gap-4', narrow ? 'flex-col' : 'flex-row items-start')}>
        <div className="min-w-0 flex-1 space-y-4">
          {/* Large */}
          <CardRow label={t('platenVariants.large')} narrow={narrow} labelClassName={admin ? 'pt-7' : undefined}>
            {admin ? (
              <>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="grid gap-1">
                    <FieldLabel>{t('platenVariants.width')}</FieldLabel>
                    <PxField
                      value={draft.width}
                      ariaLabel={t('platenVariants.ariaLw', { platen })}
                      state={fieldState(status.widthBad, widthBig, widthChanged)}
                      describedById={largeDescribedBy}
                      disabled={disabled}
                      onChange={(text) => setLarge('width', text)}
                      onBlur={() => normalizeLarge('width')}
                      suffix={t('platenVariants.px')}
                    />
                  </label>
                  <span className="pb-2.5 text-sm text-muted-foreground" aria-hidden="true">
                    ×
                  </span>
                  <label className="grid gap-1">
                    <FieldLabel>{t('platenVariants.height')}</FieldLabel>
                    <PxField
                      value={draft.height}
                      ariaLabel={t('platenVariants.ariaLh', { platen })}
                      state={fieldState(status.heightBad, heightBig, heightChanged)}
                      describedById={largeDescribedBy}
                      disabled={disabled}
                      onChange={(text) => setLarge('height', text)}
                      onBlur={() => normalizeLarge('height')}
                      suffix={t('platenVariants.px')}
                    />
                  </label>
                </div>
                {largeBad && (
                  <NoteLine as="p" tone="error" icon={AlertCircle} id={id('large-error')} className="font-medium">
                    {t('platenVariants.largeRange')}
                  </NoteLine>
                )}
                {large && <MetaLine id={id('large-meta')} parts={largeMetaParts(t, platen, large)} />}
                {big && (
                  <NoteLine as="p" tone="warning" icon={AlertTriangle} id={id('large-warn')} className="font-medium">
                    {t('platenVariants.largeBig', { a: nominal.width, b: nominal.height })}
                  </NoteLine>
                )}
              </>
            ) : (
              <>
                <p className="text-sm tabular-nums">
                  {saved.large.width} × {saved.large.height} {t('platenVariants.px')}
                </p>
                <MetaLine parts={largeMetaParts(t, platen, saved.large)} />
              </>
            )}
          </CardRow>

          <div className="border-t" aria-hidden="true" />

          {/* Default for Small */}
          <CardRow label={t('platenVariants.small')} narrow={narrow} labelClassName={admin ? 'pt-2' : undefined}>
            {admin ? (
              <>
                <SegmentedControl<SmallMode>
                  options={[
                    { value: 'hand', label: t('platenVariants.modeHand') },
                    { value: 'made', label: t('platenVariants.modeMade') },
                  ]}
                  value={draft.mode}
                  onChange={setMode}
                  ariaLabel={t('platenVariants.modeAria', { platen })}
                  tone="strong"
                  size="md"
                  disabled={disabled}
                  className={cn('flex-wrap', modeChanged && 'border-threadheads-purple')}
                />
                {draft.mode === 'hand' ? (
                  <p className="text-xs text-muted-foreground">{t('platenVariants.handLine')}</p>
                ) : (
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="grid gap-1">
                        <FieldLabel>{t('platenVariants.pctLabel')}</FieldLabel>
                        <span className="inline-flex items-center gap-1.5">
                          <Input
                            ref={pctInputRef}
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            value={draft.pct}
                            placeholder={t('platenVariants.pctPlaceholder')}
                            aria-label={t('platenVariants.ariaPct', { platen })}
                            aria-invalid={pctBad || pctEmpty || undefined}
                            aria-describedby={pctDescribedBy}
                            disabled={disabled}
                            onChange={(e) => setPct(e.target.value)}
                            onBlur={normalizePct}
                            className={cn(numberInputClass, borderFor(fieldState(pctBad, pctEmpty, pctChanged)))}
                          />
                          {draft.pct.trim() !== '' && (
                            <span className="text-sm text-muted-foreground" aria-hidden="true">
                              %
                            </span>
                          )}
                        </span>
                      </label>
                      {!narrow && (
                        <span className="pb-2.5 text-sm text-muted-foreground" aria-hidden="true">
                          =
                        </span>
                      )}
                      <div className="grid gap-1">
                        <FieldLabel>{t('platenVariants.artLabel')}</FieldLabel>
                        <span className="flex flex-wrap items-center gap-2">
                          <PxField
                            value={draft.artWidth}
                            ariaLabel={t('platenVariants.ariaSw', { platen })}
                            state={fieldState(draft.artError === 'width', false, pctChanged)}
                            describedById={artDescribedBy}
                            disabled={disabled || !large}
                            onChange={(text) => setArt('width', text)}
                            onBlur={normalizeArt}
                            suffix={t('platenVariants.px')}
                          />
                          <span className="text-sm text-muted-foreground" aria-hidden="true">
                            ×
                          </span>
                          <PxField
                            value={draft.artHeight}
                            ariaLabel={t('platenVariants.ariaSh', { platen })}
                            state={fieldState(draft.artError === 'height', false, pctChanged)}
                            describedById={artDescribedBy}
                            disabled={disabled || !large}
                            onChange={(text) => setArt('height', text)}
                            onBlur={normalizeArt}
                            suffix={t('platenVariants.px')}
                          />
                        </span>
                      </div>
                    </div>
                    {pctEmpty && (
                      <NoteLine as="p" tone="warning" icon={AlertTriangle} id={id('pct-needs')} className="font-medium">
                        {t('platenVariants.pctNeeds')}
                      </NoteLine>
                    )}
                    {pctBad && (
                      <NoteLine as="p" tone="error" icon={AlertCircle} id={id('pct-error')} className="font-medium">
                        {t('platenVariants.pctRange')}
                      </NoteLine>
                    )}
                    {large && draft.artError && (
                      <NoteLine as="p" tone="error" icon={AlertCircle} id={id('art-error')} className="font-medium">
                        {t(draft.artError === 'width' ? 'platenVariants.artRangeW' : 'platenVariants.artRangeH', {
                          ...artBounds(large, draft.artError),
                        })}
                      </NoteLine>
                    )}
                    {large && (
                      <p id={id('small-meta')} className="text-xs tabular-nums text-muted-foreground">
                        {t('platenVariants.smallMeta', { w: large.width, h: large.height })}
                      </p>
                    )}
                    <p className="max-w-prose text-xs text-muted-foreground">{t('platenVariants.madeLine')}</p>
                  </div>
                )}
              </>
            ) : saved.smallScaleBp === null ? (
              <p className="text-sm">{t('platenVariants.modeHand')}</p>
            ) : (
              <>
                <p className="text-sm tabular-nums">
                  {t('platenVariants.roMade', { pct: formatScalePercent(saved.smallScaleBp) })}
                </p>
                <p className="text-xs tabular-nums text-muted-foreground">
                  {(() => {
                    const a = smallArtPixels(saved.large, saved.smallScaleBp);
                    return t('platenVariants.roArt', {
                      w: a.width,
                      h: a.height,
                      lw: saved.large.width,
                      lh: saved.large.height,
                    });
                  })()}
                </p>
              </>
            )}
          </CardRow>
        </div>

        <PlatenPreview
          large={admin ? large : saved.large}
          fallback={d}
          narrow={narrow}
          mode={admin ? draft.mode : saved.smallScaleBp !== null ? 'made' : 'hand'}
          bp={admin ? (typeof bp === 'number' && !draft.artError ? bp : null) : saved.smallScaleBp}
          artReady={admin ? art !== null && !draft.artError : true}
        />
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Preview: a Large box at the Large aspect in a 132 x 120 frame, the Small
// art box at top centre (smallPlacement). aria-hidden as a whole: the lines
// above say the same in words.
// ---------------------------------------------------------------------------

const FRAME_W = 132;
const FRAME_H = 120;
/** A tag needs this much box (px on screen) to fit without crowding. */
const TAG_MIN_W = 30;
const TAG_MIN_H = 14;

function PlatenPreview({
  large,
  fallback,
  narrow,
  mode,
  bp,
  artReady,
}: {
  large: PlatenPixelSize | null;
  /** The platen's default Large, drawn dashed and empty while Large is invalid. */
  fallback: PlatenPixelSize;
  narrow: boolean;
  mode: SmallMode;
  bp: number | null;
  artReady: boolean;
}): JSX.Element {
  const { t } = useTranslation('settings');
  let caption: string;
  if (!large) caption = t('platenVariants.capNoLarge');
  else if (mode === 'hand') caption = t('platenVariants.capHand');
  else if (bp === null || !artReady) caption = t('platenVariants.capNotSet');
  else caption = t('platenVariants.capMade', { pct: formatScalePercent(bp) });

  const drawn = large ?? fallback;
  const scale = Math.min(FRAME_W / drawn.width, FRAME_H / drawn.height);
  const box = { width: drawn.width * scale, height: drawn.height * scale };
  const place = large && mode === 'made' && bp !== null && artReady ? smallPlacement(large, bp) : null;
  const small = place
    ? { left: place.left * scale, top: place.top * scale, width: place.width * scale, height: place.height * scale }
    : null;
  // The Large tag sits bottom left: shown only while the Small box leaves room under it.
  const showLargeTag = large !== null && (!small || box.height - (small.top + small.height) >= TAG_MIN_H);
  const showSmallTag = small !== null && small.width >= TAG_MIN_W && small.height >= TAG_MIN_H;

  return (
    <figure
      aria-hidden="true"
      className={cn('flex shrink-0 flex-col items-center gap-1.5', narrow ? 'self-center' : 'w-[148px]')}
    >
      <div
        className="flex items-center justify-center"
        style={{ width: FRAME_W, height: FRAME_H }}
        data-testid="platen-variants-preview"
      >
        {large ? (
          <div
            className="relative rounded-sm border border-line-strong bg-muted"
            style={{ width: box.width, height: box.height }}
          >
            {showLargeTag && (
              <span className="absolute bottom-0.5 left-1 text-[10px] leading-none text-muted-foreground">
                {t('platenVariants.large')}
              </span>
            )}
            {place && small && (
              <div
                className="absolute flex items-start justify-center border-[1.5px] border-primary bg-primary/15"
                data-testid="platen-variants-preview-small"
                data-left={place.left}
                data-top={place.top}
                data-width={place.width}
                data-height={place.height}
                style={small}
              >
                {showSmallTag && (
                  <span className="mt-0.5 text-[10px] leading-none text-primary-strong">
                    {t('platenVariants.tagSmall')}
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div
            className="rounded-sm border border-dashed border-line-strong"
            style={{ width: box.width, height: box.height }}
            data-testid="platen-variants-preview-empty"
          />
        )}
      </div>
      <figcaption className="text-center text-xs text-muted-foreground">{caption}</figcaption>
    </figure>
  );
}
